/*!
 * Superstratum Origin Bridge
 * ---------------------------------------------------------------------------
 * Single host-aware script for relaying session-origin data
 * (UTM params, click IDs, referrer, landing host) between
 *   - superstratumlabs.com           (apex / marketing)
 *   - calculator.superstratumlabs.com (calculator app)
 *   - shop.superstratumlabs.com      (Shopify storefront)
 *
 * On apex + calculator: captures origin into a `.superstratumlabs.com`-scoped
 *   `utm_data` JSON cookie and decorates every outbound link/form pointing at
 *   shop.superstratumlabs.com with the captured params.
 *
 * On shop: runs as early as possible in <head>; if the landing URL lacks
 *   UTMs but the cookie has them, it rewrites the URL via history.replaceState
 *   so Shopify session attribution captures them; then it pushes to
 *   window.dataLayer and posts attributes to /cart/update.js so the order
 *   carries the origin on the server side too.
 *
 * Designed to coexist with any pre-existing `utm_data` cookie writer:
 *   it only ADDS keys, never destroys.
 *
 * Install:
 *   <script src="…/superstratum-origin-bridge.js"></script>
 *   Place inside <head>, BEFORE any analytics scripts on shop.
 *   On apex/calculator, GTM Custom HTML is also fine.
 *
 * Debug:
 *   ?ssl_debug=1 in the URL prints what the script is doing to the console.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Configuration
  // -------------------------------------------------------------------------
  var APEX_DOMAIN   = '.superstratumlabs.com';
  var SHOP_HOST     = 'shop.superstratumlabs.com';
  var COOKIE_NAME   = 'utm_data';
  var COOKIE_TTL    = 60 * 60 * 24 * 90;  // 90 days
  var DEBUG         = /[?&]ssl_debug=1/.test(location.search);

  // Tracking params we will capture / forward / restore
  var UTM_PARAMS   = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','utm_id','utm_source_platform'];
  var CLICK_IDS    = ['gclid','fbclid','ttclid','msclkid','wbraid','gbraid','yclid','irclickid','epik','li_fat_id','rdt_cid','twclid','gad_source','ScCid'];
  var EXTRA_PARAMS = ['ref','source','aff_id','aff_sub'];
  var ALL_PARAMS   = UTM_PARAMS.concat(CLICK_IDS).concat(EXTRA_PARAMS);

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[ssl-origin-bridge]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\\/+^])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value)
      + '; domain=' + APEX_DOMAIN
      + '; path=/'
      + '; max-age=' + COOKIE_TTL
      + '; SameSite=Lax'
      + (location.protocol === 'https:' ? '; Secure' : '');
  }

  function parseCookieJSON(name) {
    var raw = getCookie(name);
    if (!raw) return {};
    try { return JSON.parse(raw); } catch (e) { return {}; }
  }

  function getQuery() {
    var out = {};
    var qs = location.search.replace(/^\?/, '');
    if (!qs) return out;
    qs.split('&').forEach(function (p) {
      var i = p.indexOf('=');
      if (i < 0) { out[decodeURIComponent(p)] = ''; return; }
      out[decodeURIComponent(p.slice(0, i))] = decodeURIComponent(p.slice(i + 1));
    });
    return out;
  }

  function isInternalReferrer(ref) {
    return /^https?:\/\/([a-z0-9-]+\.)?superstratumlabs\.com/i.test(ref || '');
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // -------------------------------------------------------------------------
  // 1. Origin capture (runs on every host)
  // -------------------------------------------------------------------------
  function captureOrigin() {
    var existing = parseCookieJSON(COOKIE_NAME);
    var qp = getQuery();
    var changed = false;
    var hasAnyTrackingParam = ALL_PARAMS.some(function (k) { return qp[k]; });

    // First-touch: only populate landing context if we've never seen a session before
    if (!existing.captured_at && (hasAnyTrackingParam || (document.referrer && !isInternalReferrer(document.referrer)))) {
      ALL_PARAMS.forEach(function (k) { if (qp[k]) existing[k] = qp[k]; });
      existing.captured_at      = new Date().toISOString();
      existing.landing_host     = location.hostname;
      existing.landing_path     = location.pathname + (location.search || '');
      existing.landing_referrer = document.referrer || null;
      changed = true;
      log('first-touch captured', existing);
    } else if (hasAnyTrackingParam) {
      // Last-touch: refresh UTMs / click IDs when a new campaign URL is hit
      ALL_PARAMS.forEach(function (k) {
        if (qp[k] && qp[k] !== existing[k]) { existing[k] = qp[k]; changed = true; }
      });
      if (changed) {
        existing.last_touch_at   = new Date().toISOString();
        existing.last_touch_host = location.hostname;
        log('last-touch refreshed', existing);
      }
    }

    if (changed) setCookie(COOKIE_NAME, JSON.stringify(existing));
    return existing;
  }

  // -------------------------------------------------------------------------
  // 2. Link / form decorator  (apex + calculator)
  // -------------------------------------------------------------------------
  function buildShopParams() {
    var data = parseCookieJSON(COOKIE_NAME);
    var qp = getQuery();
    var out = {};
    // Current-URL UTMs take precedence so a campaign click from apex still
    // wins over an older cookie value (last-touch on the same hop).
    UTM_PARAMS.concat(CLICK_IDS).forEach(function (k) {
      var v = qp[k] || data[k];
      if (v) out[k] = v;
    });
    if (data.captured_at)  out.ssl_origin_at   = data.captured_at;
    if (data.landing_host) out.ssl_origin_host = data.landing_host;
    if (data.landing_path) out.ssl_origin_path = data.landing_path;
    return out;
  }

  function decorateUrl(urlStr) {
    var u;
    try { u = new URL(urlStr, location.href); } catch (e) { return urlStr; }
    if (u.hostname !== SHOP_HOST) return urlStr;
    var params = buildShopParams();
    var search = new URLSearchParams(u.search);
    Object.keys(params).forEach(function (k) {
      if (!search.has(k)) search.set(k, params[k]);
    });
    u.search = search.toString();
    return u.toString();
  }

  function decorateAnchor(a) {
    try {
      var href = a.getAttribute('href');
      if (!href || /^(mailto:|tel:|javascript:|#)/i.test(href)) return;
      if (a.getAttribute('data-ssl-decorated') === '1') return;
      var newHref = decorateUrl(href);
      if (newHref !== href) {
        a.setAttribute('href', newHref);
        a.setAttribute('data-ssl-decorated', '1');
      }
    } catch (e) {}
  }

  function decorateForm(form) {
    try {
      var action = form.getAttribute('action');
      if (!action) return;
      if (form.getAttribute('data-ssl-decorated') === '1') return;
      var u; try { u = new URL(action, location.href); } catch (e) { return; }
      if (u.hostname !== SHOP_HOST) return;
      var params = buildShopParams();
      Object.keys(params).forEach(function (k) {
        if (form.querySelector('input[name="' + k + '"]')) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = params[k];
        form.appendChild(input);
      });
      form.setAttribute('data-ssl-decorated', '1');
    } catch (e) {}
  }

  function decorateAll() {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) decorateAnchor(anchors[i]);
    var forms = document.querySelectorAll('form[action]');
    for (var j = 0; j < forms.length; j++) decorateForm(forms[j]);
    log('decorated initial links', anchors.length, 'forms', forms.length);
  }

  function watchMutations() {
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        if (!m.addedNodes) return;
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'A' && n.hasAttribute('href')) decorateAnchor(n);
          else if (n.tagName === 'FORM' && n.hasAttribute('action')) decorateForm(n);
          else if (n.querySelectorAll) {
            n.querySelectorAll('a[href]').forEach(decorateAnchor);
            n.querySelectorAll('form[action]').forEach(decorateForm);
          }
        });
      });
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function backstopClick() {
    // Catches programmatic .click() and last-second href swaps.
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (a) decorateAnchor(a);
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.tagName === 'FORM') decorateForm(f);
    }, true);
  }

  // -------------------------------------------------------------------------
  // 3. Shop-side reconciler
  // -------------------------------------------------------------------------
  function reconcileShopUrl() {
    var data = parseCookieJSON(COOKIE_NAME);
    if (!data || !Object.keys(data).length) { log('shop reconcile: no cookie data'); return false; }
    var qp = getQuery();
    var alreadyHasUtm = UTM_PARAMS.some(function (k) { return qp[k]; });
    if (alreadyHasUtm) { log('shop reconcile: URL already has UTMs'); return false; }

    var params = new URLSearchParams(location.search);
    var added = 0;
    UTM_PARAMS.concat(CLICK_IDS).forEach(function (k) {
      if (data[k] && !params.has(k)) { params.set(k, data[k]); added++; }
    });
    if (!added) return false;
    var newQs = params.toString();
    var newUrl = location.pathname + (newQs ? '?' + newQs : '') + location.hash;
    try {
      history.replaceState(history.state, '', newUrl);
      log('shop reconcile: URL rewritten with', added, 'params');
      return true;
    } catch (e) { log('replaceState failed', e); return false; }
  }

  function pushDataLayer() {
    var data = parseCookieJSON(COOKIE_NAME);
    if (!data || !Object.keys(data).length) return;
    window.dataLayer = window.dataLayer || [];
    var payload = { event: 'ssl_origin_ready' };
    Object.keys(data).forEach(function (k) { payload['ssl_' + k] = data[k]; });
    window.dataLayer.push(payload);
    log('dataLayer pushed', payload);
  }

  function stampCartAttributes() {
    if (!window.fetch) return;
    var data = parseCookieJSON(COOKIE_NAME);
    if (!data || !Object.keys(data).length) return;
    var pairs = [];
    Object.keys(data).forEach(function (k) {
      pairs.push(
        encodeURIComponent('attributes[ssl_' + k + ']') + '=' +
        encodeURIComponent(String(data[k] == null ? '' : data[k]))
      );
    });
    if (!pairs.length) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: pairs.join('&'),
      credentials: 'same-origin',
      keepalive: true
    }).then(function (r) { log('cart.attributes stamped', r.status); })
      .catch(function (e) { log('cart.attributes error', e); });
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------
  // Always capture first — works on all three hosts.
  captureOrigin();

  if (location.hostname === SHOP_HOST) {
    // Reconcile URL SYNC, before Shopify analytics beacons fire.
    reconcileShopUrl();
    // Push to dataLayer SYNC too (so GTM tags reading dataLayer see it).
    pushDataLayer();
    // Cart attribute stamp can wait for DOMContentLoaded.
    onReady(stampCartAttributes);
  } else {
    onReady(function () {
      decorateAll();
      watchMutations();
      backstopClick();
    });
  }
})();
